import { NetworkError } from '@recipes/api-client';
import type { Recipe } from '@recipes/contracts';
import { useLiveQuery } from 'dexie-react-hooks';
import { useEffect, useState } from 'react';
import { offlineDb } from './db';
import { resolveOfflineImageUrl, type OfflineImageVariant } from './image-url';
import { offlineManager } from './manager';
import { isOnline, useOnlineStatus } from '@/lib/hooks/use-online-status';

export class OfflineUnavailableError extends Error {
  constructor(public readonly recipeId: string) {
    super('Recipe is not available offline');
    this.name = 'OfflineUnavailableError';
  }
}

export interface RecipeReadResult {
  recipe: Recipe;
  /** True when the data came from the local offline copy instead of the server. */
  offlineCopy: boolean;
  savedAt: number | null;
}

/**
 * Read a recipe from the API, falling back to the offline copy when the device is
 * offline or the network request fails. Used as the queryFn for recipe detail.
 */
export async function readRecipeWithFallback(
  recipeId: string,
  fetchRemote: () => Promise<Recipe>,
): Promise<RecipeReadResult> {
  const readLocal = async (): Promise<RecipeReadResult> => {
    const local = await offlineManager.getRecipe(recipeId);
    if (!local) throw new OfflineUnavailableError(recipeId);
    return { recipe: local.recipe, offlineCopy: true, savedAt: local.savedAt };
  };
  if (!isOnline()) return readLocal();
  try {
    const recipe = await fetchRemote();
    return { recipe, offlineCopy: false, savedAt: null };
  } catch (error) {
    if (error instanceof NetworkError) return readLocal();
    throw error;
  }
}

/** Reactive "is this recipe downloaded" flag. */
export function useIsDownloaded(recipeId: string | undefined): boolean {
  const downloaded = useLiveQuery(
    () => (recipeId ? offlineDb.offlineRecipes.get(recipeId) : Promise.resolve(undefined)),
    [recipeId],
  );
  return downloaded !== undefined && downloaded !== null;
}

export function usePendingActionCount(): number {
  return useLiveQuery(() => offlineDb.pendingActions.count(), [], 0) ?? 0;
}

export function useSyncConflicts() {
  return useLiveQuery(() => offlineDb.syncConflicts.orderBy('createdAt').toArray(), [], []) ?? [];
}

export function useOfflineRecipes() {
  return useLiveQuery(() => offlineManager.list(), [], undefined);
}

/**
 * Image source that prefers the stored blob when offline (or when explicitly showing an
 * offline copy) and the signed URL otherwise.
 */
export function useImageSrc(
  url: string | null | undefined,
  assetId: string | null | undefined,
  variant: OfflineImageVariant = 'medium',
  preferOffline = false,
): string | null {
  const online = useOnlineStatus();
  const useBlob = (!online || preferOffline) && !!assetId;
  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  useEffect(() => {
    let cancelled = false;
    if (!useBlob || !assetId) {
      setBlobUrl(null);
      return;
    }
    void resolveOfflineImageUrl(assetId, variant).then((resolved) => {
      if (!cancelled) setBlobUrl(resolved);
    });
    return () => {
      cancelled = true;
    };
  }, [useBlob, assetId, variant]);
  if (useBlob && blobUrl) return blobUrl;
  return url ?? null;
}
